import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiClock,
  FiArrowLeft,
  FiArrowRight,
  FiFlag,
  FiAlertTriangle,
  FiPause,
  FiPlay,
  FiSend,
  FiEye,
  FiEyeOff,
  FiHome,
  FiBookOpen,
  FiBarChart2,
  FiRefreshCw
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import ExamModeSelector from '../../components/tests/ExamModeSelector';
import PracticeTestCover from '../../components/tests/PracticeTestCover';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { testAPI } from '../../services/api';
import './TakeTest.css';

const TakePracticeTest = () => {
  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [isTestPaused, setIsTestPaused] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [loading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [error, setError] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [showExplanation, setShowExplanation] = useState({});
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [examMode, setExamMode] = useState(null);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [showCover, setShowCover] = useState(true);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [showComprehensionPassage, setShowComprehensionPassage] = useState(true);

  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const timerRef = useRef(null);
  const saveProgressTimeoutRef = useRef(null);
  const hasRedirectedToResultsRef = useRef(false);

  // Utility function to shuffle array
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Generate unique session key for this exam attempt (stable)
  const generateSessionKey = useCallback((testDataArg, examModeArg) => {
    return `exam_session_${testDataArg?.subject}_${testDataArg?.examType}_${examModeArg}_${user?.id || 'anonymous'}`;
  }, [user?.id]);

  // Save exam session to localStorage and (debounced) to backend
  const saveExamSession = useCallback((sessionData) => {
    if (!testData || !examMode) return;

    const sessionKey = generateSessionKey(testData, examMode);
    const sessionToSave = {
      ...sessionData,
      timestamp: Date.now(),
      isTestStarted: isTestStarted,
      examMode,
      testData: {
        ...testData,
        originalQuestions: testData.originalQuestions || questions
      }
    };

    try {
      localStorage.setItem(sessionKey, JSON.stringify(sessionToSave));
    } catch (error) {
      console.error('Failed to save exam session:', error);
    }

    // Debounced save to backend (so progress persists if they leave and come back on another device)
    if (saveProgressTimeoutRef.current) clearTimeout(saveProgressTimeoutRef.current);
    saveProgressTimeoutRef.current = setTimeout(() => {
      saveProgressTimeoutRef.current = null;
      const qs = sessionData.questions !== undefined ? sessionData.questions : questions;
      const flagged = Array.isArray(sessionData.flaggedQuestions)
        ? sessionData.flaggedQuestions
        : (sessionData.flaggedQuestions instanceof Set ? Array.from(sessionData.flaggedQuestions) : []);
      testAPI.savePracticeProgress({
        subject: testData.subject,
        examType: testData.examType,
        examMode,
        questionData: qs,
        answers: sessionData.answers || {},
        currentQuestionIndex: sessionData.currentQuestionIndex ?? 0,
        flaggedQuestions: flagged,
        timeRemaining: sessionData.timeRemaining ?? 0,
        timeLimitMinutes: testData.timeLimit ?? 30,
        isStarted: isTestStarted,
        showExplanation: sessionData.showExplanation || {}
      }).catch(() => {});
    }, 2000);
  }, [testData, examMode, isTestStarted, questions, generateSessionKey]);

  // (Removed unused loadExamSession helper)

  // Clear exam session from localStorage (stable)
  const clearExamSession = useCallback(() => {
    if (!testData || !examMode) return;
    const sessionKey = generateSessionKey(testData, examMode);
    try {
      localStorage.removeItem(sessionKey);
    } catch (error) {
      console.error('Failed to clear exam session:', error);
    }
  }, [testData, examMode, generateSessionKey]);

  // Check for existing sessions on component mount (backend + localStorage)
  useEffect(() => {
    const checkForExistingSessions = () => {
      if (!user?.id) return null;
      const keys = Object.keys(localStorage);
      const sessionKeys = keys.filter(key => key.startsWith(`exam_session_`) && key.includes(user.id));
      if (sessionKeys.length === 0) return null;
      let mostRecentSession = null;
      let mostRecentTimestamp = 0;
      sessionKeys.forEach(key => {
        try {
          const session = JSON.parse(localStorage.getItem(key));
          if (session?.timestamp > mostRecentTimestamp) {
            mostRecentSession = session;
            mostRecentTimestamp = session.timestamp;
          }
        } catch (e) {}
      });
      return mostRecentSession ? { session: mostRecentSession } : null;
    };

    const applyProgress = (progress) => {
      if (!progress?.questionData?.length) return false;
      const subject = progress.subject;
      const examType = progress.examType;
      const newTestData = {
        subject,
        examType,
        title: `${subject} Practice Test (${examType})`,
        description: `Practice test for ${subject} - ${examType} examination`,
        timeLimit: progress.timeLimitMinutes || 30,
        originalQuestions: progress.questionData,
        questionData: progress.questionData
      };
      setTestData(newTestData);
      setExamMode(progress.examMode || 'practice');
      setQuestions(progress.questionData);
      setAnswers(progress.answers || {});
      setFlaggedQuestions(new Set(progress.flaggedQuestions || []));
      setCurrentQuestionIndex(progress.currentQuestionIndex ?? 0);
      setTimeRemaining(progress.timeRemaining ?? (progress.timeLimitMinutes || 30) * 60);
      setShowExplanation(progress.showExplanation || {});
      setHasExistingSession(true);
      if (progress.isStarted) setIsTestStarted(true);
      setShowComprehensionPassage(progress.comprehensionPassage && !progress.isStarted);
      return true;
    };

    // Restore from backend progress passed in state (PracticeTests found in-progress)
    if (location.state?.savedProgress && location.state?.progressData) {
      applyProgress(location.state.progressData);
      return;
    }

    // Navigation state with testData (user clicked Start from list)
    if (location.state?.testData) {
      const { testData: data, subject, examType, mode } = location.state;
      const originalQuestions = data?.questionData || data?.questions || [];
      const newTestData = {
        ...data,
        subject,
        examType,
        title: `${subject} Practice Test (${examType})`,
        description: `Practice test for ${subject} - ${examType} examination`,
        timeLimit: Math.max(originalQuestions.length * 2, 30),
        originalQuestions
      };
      setTestData(newTestData);
      // Only set examMode from state when explicitly passed (e.g. resume); otherwise show "Choose Your Exam Mode" modal
      setExamMode(mode !== undefined && mode !== null ? mode : null);

      (async () => {
        // Prefer backend progress for this subject+examType, then localStorage
        try {
          const res = await testAPI.getPracticeProgress({ subject, examType });
          const progress = res.data?.data;
          if (progress?.isStarted && progress?.questionData?.length) {
            applyProgress(progress);
            return;
          }
        } catch (e) {}
        const sessionKey = `exam_session_${subject}_${examType}_${mode || 'practice'}_${user?.id || 'anonymous'}`;
        try {
          const savedSession = localStorage.getItem(sessionKey);
          if (savedSession) {
            const existingSession = JSON.parse(savedSession);
            if (existingSession?.questions?.length) {
              setQuestions(existingSession.questions);
              setAnswers(existingSession.answers || {});
              setFlaggedQuestions(new Set(existingSession.flaggedQuestions || []));
              setCurrentQuestionIndex(existingSession.currentQuestionIndex || 0);
              setTimeRemaining(existingSession.timeRemaining ?? Math.max(originalQuestions.length * 2, 30) * 60);
              setShowExplanation(existingSession.showExplanation || {});
              setHasExistingSession(true);
              if (existingSession.isTestStarted) {
                setIsTestStarted(true);
                setExamMode(existingSession.examMode || mode || 'practice');
              }
              return;
            }
          }
        } catch (e) {}
        setQuestions(shuffleArray(originalQuestions));
        setTimeRemaining(Math.max(originalQuestions.length * 2, 30) * 60);
        setHasExistingSession(false);
      })();
      return;
    }

    // No navigation state (refresh or direct URL) – try backend then localStorage
    (async () => {
      try {
        const res = await testAPI.getPracticeProgress();
        const progress = res.data?.data;
        if (progress && applyProgress(progress)) return;
      } catch (e) {}
      const existingSessionData = checkForExistingSessions();
      if (existingSessionData?.session) {
        const session = existingSessionData.session;
        if (session.testData) {
          setTestData(session.testData);
          setExamMode(session.examMode);
          setQuestions(session.questions || []);
          setAnswers(session.answers || {});
          setFlaggedQuestions(new Set(session.flaggedQuestions || []));
          setCurrentQuestionIndex(session.currentQuestionIndex || 0);
          setTimeRemaining(session.timeRemaining || 0);
          setShowExplanation(session.showExplanation || {});
          setHasExistingSession(true);
          if (session.isTestStarted) setIsTestStarted(true);
        } else setError('No test data available. Please start a new test.');
      } else setError('No test data provided. Please start a new test.');
    })();
  }, [location.state, user?.id]);

  // Check if exam mode was passed from navigation or show selector
  useEffect(() => {
    if (testData && !examMode && !hasExistingSession) {
      setShowModeSelector(true);
    } else if (testData && examMode && hasExistingSession) {
      setShowModeSelector(false);
    } else if (testData && hasExistingSession && !examMode) {
      setExamMode('practice');
    }
  }, [testData, examMode, hasExistingSession]);

  // Ensure we redirect to results page when we have a submission id (handles Railway/proxy response shape)
  useEffect(() => {
    if (!isTestCompleted) {
      hasRedirectedToResultsRef.current = false;
      return;
    }
    if (hasRedirectedToResultsRef.current || !testResults?.submissionId) return;
    const sid = testResults.submissionId;
    const id = typeof sid === 'string' ? sid : (sid?.toString?.() ?? sid?._id ?? String(sid));
    if (id) {
      hasRedirectedToResultsRef.current = true;
      navigate(`/practice-tests/${id}/results`, { replace: true });
    }
  }, [isTestCompleted, testResults, navigate]);

  // Timer controls and submission handlers (memoized and ordered to satisfy lints)
  // stopTimer memoized above

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const calculateResults = useCallback(() => {
    let correctAnswers = 0;
    let totalQuestions = questions.length;
    const questionResults = [];

    questions.forEach((question, index) => {
      const userAnswer = answers[index];
      const isCorrect = userAnswer === question.correctAnswer;
      if (isCorrect) correctAnswers++;
      questionResults.push({
        questionIndex: index,
        question: question.question,
        userAnswer,
        correctAnswer: question.correctAnswer,
        isCorrect,
        explanation: question.explanation,
        options: question.options
      });
    });

    const score = Math.round((correctAnswers / totalQuestions) * 100);
    const timeSpent = (testData.timeLimit * 60) - timeRemaining;
    return { score, totalQuestions, correctAnswers, timeSpent, questionResults };
  }, [questions, answers, testData, timeRemaining]);

  const handleSubmitTest = useCallback(async () => {
    console.log('[AI INSIGHT] BEFORE: Preparing to call submit practice test API.');
    try {
      setSubmitting(true);
      setSubmitError(null);
      const results = calculateResults();
      const formattedAnswers = {};
      Object.keys(answers).forEach(questionIndex => {
        const question = questions[parseInt(questionIndex)];
        if (question && question._id) {
          formattedAnswers[question._id] = answers[questionIndex];
        }
      });
      const submissionData = {
        subject: testData.subject,
        examType: testData.examType,
        questions: questions,
        answers: formattedAnswers,
        timeSpent: (testData.timeLimit * 60) - timeRemaining,
        flaggedQuestions: Array.from(flaggedQuestions)
      };
      console.log('[AI INSIGHT] DURING: Calling submit practice test API (AI insight generated on server)...');
      const response = await testAPI.submitPracticeTest(submissionData);
      const apiResult = response?.data;
      const isSuccess = apiResult?.success === true || (apiResult?.submissionId != null);
      const data = apiResult?.data ?? apiResult;
      const rawId = data?.submissionId ?? data?._id ?? data?.id ?? apiResult?.submissionId;
      const id = rawId == null ? null : (typeof rawId === 'string' ? rawId : (rawId.toString?.() ?? rawId._id ?? String(rawId)));

      if (isSuccess || id) {
        stopTimer();
        clearExamSession();
        showSuccess('Test submitted successfully!');
        if (id) {
          navigate(`/practice-tests/${id}/results`, { replace: true });
          return;
        }
        const enhancedResults = {
          ...results,
          submissionId: data?.submissionId ?? rawId,
          aiInsights: data?.aiInsights ?? apiResult?.aiInsights,
          analytics: data?.analytics ?? apiResult?.analytics,
          completedAt: data?.completedAt ?? apiResult?.completedAt
        };
        setTestResults(enhancedResults);
        setIsTestCompleted(true);
        const fallbackId = (enhancedResults.submissionId != null ? (typeof enhancedResults.submissionId === 'string' ? enhancedResults.submissionId : enhancedResults.submissionId?.toString?.()) : null) || id;
        if (fallbackId) setTimeout(() => navigate(`/practice-tests/${fallbackId}/results`, { replace: true }), 150);
      } else {
        throw new Error(apiResult?.message || 'Failed to submit test');
      }
    } catch (err) {
      const data = err?.response?.data;
      console.error('[AI INSIGHT] AFTER: Submit practice test API failed.', err?.message || err);
      if (data) {
        console.error('[AI INSIGHT] Server error message:', data.error || data.message);
        if (data.validationErrors) console.error('[AI INSIGHT] Validation errors:', data.validationErrors);
        if (data.stack) console.error('[AI INSIGHT] Server stack trace:', data.stack);
        if (data.error && !data.stack) console.error('[AI INSIGHT] Full response data (expand to see stack if present):', JSON.stringify(data, null, 2));
      }
      stopTimer();
      clearExamSession();
      setSubmitError(data?.error || data?.message || err?.message || 'Failed to save results and generate insight. Please try again.');
      showError('Could not save results or generate AI insight. Please try again.');
      // Do not set test results or isTestCompleted so the screen stays in submitting/error state until success
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  }, [calculateResults, answers, questions, testData, timeRemaining, flaggedQuestions, stopTimer, clearExamSession, showSuccess, showError, navigate]);

  // handleTimeUp memoized above

  const handleTimeUp = useCallback(() => {
    stopTimer();
    setIsTestCompleted(true);
    showWarning('Time is up! Your test has been automatically submitted.');
    handleSubmitTest();
  }, [stopTimer, showWarning, handleSubmitTest]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev <= 1 ? 0 : prev - 1;
        
        // Save session with updated time
        saveExamSession({
          questions,
          answers,
          flaggedQuestions: Array.from(flaggedQuestions),
          currentQuestionIndex,
          timeRemaining: newTime,
          showExplanation
        });
        
        if (newTime <= 1) {
          handleTimeUp();
          return 0;
        }
        return newTime;
      });
    }, 1000);
  }, [questions, answers, flaggedQuestions, currentQuestionIndex, showExplanation, saveExamSession, handleTimeUp]);

  // stopTimer and handleTimeUp defined earlier via useCallback

  const startTest = () => {
    setIsTestStarted(true);
    setIsTestPaused(false);
    startTimer();
    
    // Save session immediately when test starts
    saveExamSession({
      questions,
      answers,
      flaggedQuestions: Array.from(flaggedQuestions),
      currentQuestionIndex,
      timeRemaining,
      showExplanation
    });
  };

  const pauseTest = () => {
    setIsTestPaused(true);
    stopTimer();
  };

  const resumeTest = () => {
    setIsTestPaused(false);
    startTimer();
  };

  const handleAnswerSelect = (questionIndex, selectedOption) => {
    // In explanation mode, prevent changing answers once selected
    if (examMode === 'explanation' && answers[questionIndex] !== undefined) {
      return;
    }

    const newAnswers = {
      ...answers,
      [questionIndex]: selectedOption
    };

    setAnswers(newAnswers);

    // In explanation mode, automatically show explanation after answer selection
    if (examMode === 'explanation') {
      setShowExplanation(prev => ({
        ...prev,
        [questionIndex]: true
      }));
    }

    // Save session after answer selection
    saveExamSession({
      questions,
      answers: newAnswers,
      flaggedQuestions: Array.from(flaggedQuestions),
      currentQuestionIndex,
      timeRemaining,
      showExplanation
    });
  };

  const handleFlagQuestion = (questionIndex) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionIndex)) {
        newSet.delete(questionIndex);
      } else {
        newSet.add(questionIndex);
      }
      
      // Save session after flagging
      saveExamSession({
        questions,
        answers,
        flaggedQuestions: Array.from(newSet),
        currentQuestionIndex,
        timeRemaining,
        showExplanation
      });
      
      return newSet;
    });
  };

  const navigateToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
      
      // Save session after navigation
      saveExamSession({
        questions,
        answers,
        flaggedQuestions: Array.from(flaggedQuestions),
        currentQuestionIndex: index,
        timeRemaining,
        showExplanation
      });
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      
      // Save session when navigating
      saveExamSession({
        questions,
        answers,
        flaggedQuestions: Array.from(flaggedQuestions),
        currentQuestionIndex: newIndex,
        timeRemaining,
        showExplanation
      });
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      
      // Save session when navigating
      saveExamSession({
        questions,
        answers,
        flaggedQuestions: Array.from(flaggedQuestions),
        currentQuestionIndex: newIndex,
        timeRemaining,
        showExplanation
      });
    }
  };

  // handleSubmitTest memoized above

  // calculateResults defined earlier via useCallback

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    const percentage = (timeRemaining / (testData?.timeLimit * 60)) * 100;
    if (percentage <= 10) return 'danger';
    if (percentage <= 25) return 'warning';
    return 'primary';
  };

  const toggleExplanation = (questionIndex) => {
    setShowExplanation(prev => ({
      ...prev,
      [questionIndex]: !prev[questionIndex]
    }));
  };

  const getQuestionStatus = (index) => {
    if (answers[index] !== undefined) return 'answered';
    if (flaggedQuestions.has(index)) return 'flagged';
    return 'unanswered';
  };

  // (Removed unused getStatusColor helper)

  if (error) {
    return (
      <div className="take-test-page">
        <div className="error-container">
          <div className="error-content">
            <FiAlertTriangle className="error-icon" />
            <h3>Error Loading Practice Test</h3>
            <p>{error}</p>
            <Button onClick={() => navigate('/practice-tests')} variant="primary">
              <FiHome />
              Back to Practice Tests
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!testData || loading) {
    return (
      <div className="take-test-page">
        <div className="loading-container">
          <LoadingSpinner size="lg" />
          <p>Loading practice test...</p>
        </div>
      </div>
    );
  }

  // Submitting: show dedicated loading until backend returns (insight generation can take a minute)
  if (submitting) {
    return (
      <div className="take-test-page">
        <div className="loading-container submitting-insight">
          <LoadingSpinner size="lg" />
          <p className="submitting-title">Submitting your test</p>
          <p className="submitting-message">Generating Expert Intelligence insight… This may take up to a minute.</p>
          <p className="submitting-hint">Please don&apos;t close this page.</p>
        </div>
      </div>
    );
  }

  // Submit failed: do not show results; show error and allow retry or leave
  if (submitError && !submitting) {
    return (
      <div className="take-test-page">
        <div className="test-results-container" style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Submission failed</h2>
          <p className="text-secondary" style={{ margin: '1rem 0' }}>{submitError}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={() => { setSubmitError(null); handleSubmitTest(); }}>
              <FiRefreshCw /> Try again
            </Button>
            <Button variant="outline" onClick={() => navigate('/practice-tests')}>
              <FiBookOpen /> Back to Practice Tests
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Test Results View (only after submit API success and AI insight complete)
  if (isTestCompleted && testResults) {
    return (
      <div className="take-test-page">
        <div className="test-results-container">
          <div className="results-header">
            <h1>Practice Test Results</h1>
            <div className="test-info">
              <Badge variant="primary">{testData.subject}</Badge>
              <Badge variant="secondary">{testData.examType}</Badge>
            </div>
          </div>

          <div className="results-summary">
            <div className="score-display">
              <div className="score-circle">
                <span className="score-value">{testResults.score}%</span>
              </div>
              <div className="score-details">
                <p>{testResults.correctAnswers} out of {testResults.totalQuestions} correct</p>
                <p>Time spent: {formatTime(testResults.timeSpent)}</p>
              </div>
            </div>
            
            {/* Expert Intelligence Insights Section - always show heading; content or fallback */}
            <div className="ai-insights">
              <h3>Expert Intelligence Insight</h3>
              {testResults.aiInsights ? (
               <div className="insights-content">
                   {testResults.aiInsights.readinessLevel && (
                     <div className="insight-item">
                       <h4>Exam Readiness</h4>
                       <p>
                         {testResults.aiInsights.readinessLevel === 'exam_ready' && 'You are ready for the exam! Keep up the excellent work.'}
                         {testResults.aiInsights.readinessLevel === 'almost_ready' && 'You are almost ready for the exam. Focus on your weak areas.'}
                         {testResults.aiInsights.readinessLevel === 'needs_improvement' && 'You need more preparation before taking the exam.'}
                         {testResults.aiInsights.readinessLevel === 'not_ready' && 'Significant preparation is needed before attempting the exam.'}
                       </p>
                     </div>
                   )}
                   
                   {testResults.aiInsights.strengths && testResults.aiInsights.strengths.length > 0 && (
                     <div className="insight-item">
                       <h4>Your Strengths</h4>
                       <ul>
                         {testResults.aiInsights.strengths.map((strength, index) => (
                           <li key={index}>{strength.description}</li>
                         ))}
                       </ul>
                     </div>
                   )}
                   
                   {testResults.aiInsights.weaknesses && testResults.aiInsights.weaknesses.length > 0 && (
                     <div className="insight-item">
                       <h4>Areas for Improvement</h4>
                       <ul>
                         {testResults.aiInsights.weaknesses.map((weakness, index) => (
                           <li key={index}>
                             <strong>{weakness.area}:</strong> {weakness.description}
                           </li>
                         ))}
                       </ul>
                     </div>
                   )}
                   
                   {testResults.aiInsights.aiRecommendations && testResults.aiInsights.aiRecommendations.length > 0 && (
                     <div className="insight-item">
                       <h4>Expert Intelligence Study Recommendations</h4>
                       <ul>
                         {testResults.aiInsights.aiRecommendations.map((recommendation, index) => (
                           <li key={index}>{recommendation}</li>
                         ))}
                       </ul>
                     </div>
                   )}
                   
                   {testResults.aiInsights.studyPlan && testResults.aiInsights.studyPlan.length > 0 && (
                     <div className="insight-item">
                       <h4>Personalized Study Plan</h4>
                       <ul>
                         {testResults.aiInsights.studyPlan.map((plan, index) => (
                           <li key={index}>
                             <strong>{plan.topic}</strong> - {plan.estimatedStudyTime} hours 
                             ({plan.priority} priority)
                           </li>
                         ))}
                       </ul>
                     </div>
                   )}
                   
                   {testResults.aiInsights.predictedExamScore && (
                     <div className="insight-item">
                       <h4>Predicted Exam Scores</h4>
                       <p>
                         Based on your performance, here are your predicted scores:
                       </p>
                       <ul>
                         {testResults.aiInsights.predictedExamScore.waec && (
                           <li>WAEC: {testResults.aiInsights.predictedExamScore.waec}%</li>
                         )}
                         {testResults.aiInsights.predictedExamScore.jamb && (
                           <li>JAMB: {testResults.aiInsights.predictedExamScore.jamb}/400</li>
                         )}
                         {testResults.aiInsights.predictedExamScore.postUme && (
                           <li>Post-UME: {testResults.aiInsights.predictedExamScore.postUme}%</li>
                         )}
                       </ul>
                     </div>
                   )}
                 </div>
              ) : (
                <p className="ai-insight-fallback">
                  Expert Intelligence analysis is available when a syllabus is set up for this subject. Review your answers below to see explanations.
                </p>
              )}
            </div>
          </div>

          <div className="results-actions">
            <Button
              variant="primary"
              onClick={() => navigate('/dashboard')}
            >
              <FiHome />
              Back to Home
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/practice-tests')}
            >
              <FiBookOpen />
              Back to Practice Tests
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowReview(true)}
            >
              <FiEye />
              Review Answers
            </Button>
          </div>

          {showReview && (
            <div className="review-section">
              <h3>Answer Review</h3>
              <div className="review-questions">
                {testResults.questionResults.map((result, index) => (
                  <div key={index} className={`review-question ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="question-header">
                      <span className="question-number">Question {index + 1}</span>
                      <Badge variant={result.isCorrect ? 'success' : 'danger'}>
                        {result.isCorrect ? 'Correct' : 'Incorrect'}
                      </Badge>
                    </div>
                    <div className="question-text">
                      {typeof result.question === 'string' 
                        ? result.question 
                        : result.question?.text || 'Question text not available'}
                    </div>
                    <div className="answer-comparison">
                      <div className="user-answer">
                        <strong>Your answer:</strong> {
                          result.userAnswer !== null && result.userAnswer !== undefined
                            ? (typeof result.options[result.userAnswer] === 'string' 
                                ? result.options[result.userAnswer]
                                : result.options[result.userAnswer]?.text || 'Option text not available')
                            : 'Not answered'
                        }
                      </div>
                      <div className="correct-answer">
                        <strong>Correct answer:</strong> {
                          typeof result.options[result.correctAnswer] === 'string'
                            ? result.options[result.correctAnswer]
                            : result.options[result.correctAnswer]?.text || 'Option text not available'
                        }
                      </div>
                    </div>
                    {result.explanation && (
                      <div className="explanation">
                        <strong>Explanation:</strong> {
                          typeof result.explanation === 'string'
                            ? result.explanation
                            : result.explanation?.text || 'Explanation not available'
                        }
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pre-test view: order is (1) Choose exam mode → (2) Cover → (3) Questions
  if (!isTestStarted) {
    // Step 1: Show only "Choose Your Exam Mode" (before cover)
    if (!examMode && !hasExistingSession && showModeSelector) {
      return (
        <div className="take-test-page">
          <ExamModeSelector
            testTitle={testData.title}
            testDuration={(testData.timeLimit || 30) * 60}
            questionCount={questions.length}
            onModeSelect={(mode) => {
              setExamMode(mode);
              setShowModeSelector(false);
            }}
            onClose={() => {
              setShowModeSelector(false);
              navigate('/practice-tests');
            }}
          />
        </div>
      );
    }

    // Step 2: WAEC-style cover (after mode selected, before questions)
    if (showCover && examMode && !hasExistingSession) {
      const candidateName = user?.firstName || user?.fullName ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.fullName : '';
      return (
        <div className="take-test-page">
          <PracticeTestCover
            subject={testData.subject}
            examType={testData.examType}
            timeLimitMinutes={testData.timeLimit}
            questionCount={questions.length}
            paperLabel="1"
            sessionLabel={`Practice · ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`}
            candidateName={candidateName}
            onStart={() => {
              setShowCover(false);
              if (testData.comprehensionPassage) {
                setShowComprehensionPassage(true);
              } else {
                startTest();
              }
            }}
            onBack={() => navigate('/practice-tests')}
          />
        </div>
      );
    }

    // Comprehension passage: read first, then "Continue to questions" to start timer and show questions
    if (!isTestStarted && testData?.comprehensionPassage && showComprehensionPassage) {
      return (
        <div className="take-test-page">
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Read the passage below</h2>
              <p className="text-sm text-slate-600 mb-4">After reading, click &quot;Continue to questions&quot; to answer questions based on this passage.</p>
              <div className="prose prose-slate max-w-none text-slate-700 whitespace-pre-wrap border-t border-slate-100 pt-4 mt-4">
                {testData.comprehensionPassage}
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/practice-tests')}>
                <FiArrowLeft /> Back
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  setShowComprehensionPassage(false);
                  startTest();
                }}
              >
                <FiArrowRight /> Continue to questions
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="take-test-page">
        <div className="test-intro">
          <div className="intro-header">
            <h1>{testData.title}</h1>
            <p>{testData.description}</p>
          </div>

          <div className="test-details">
            <div className="detail-item">
              <FiBookOpen className="detail-icon" />
              <div>
                <strong>Questions:</strong>
                <span>{questions.length}</span>
              </div>
            </div>
            <div className="detail-item">
              <FiClock className="detail-icon" />
              <div>
                <strong>Time Limit:</strong>
                <span>{testData.timeLimit} minutes</span>
              </div>
            </div>
            <div className="detail-item">
              <FiBarChart2 className="detail-icon" />
              <div>
                <strong>Type:</strong>
                <span>Practice Test</span>
              </div>
            </div>
          </div>

          <div className="test-instructions">
            <h3>Instructions</h3>
            <ul>
              <li>This is a practice test to help you prepare for your exam</li>
              <li>You have {testData.timeLimit} minutes to complete all questions</li>
              <li>You can flag questions for review and navigate between questions</li>
              <li>Your results will be shown immediately after submission</li>
              <li>You can pause and resume the test at any time</li>
              {hasExistingSession && (
                <li><strong>You have a saved session - click Resume to continue where you left off</strong></li>
              )}
            </ul>
          </div>

          <div className="intro-actions">
            <Button
              variant="outline"
              onClick={() => navigate('/practice-tests')}
            >
              <FiArrowLeft />
              Back
            </Button>
            <Button
              variant="primary"
              onClick={startTest}
              size="lg"
            >
              <FiPlay />
              {hasExistingSession ? 'Resume Practice Test' : 'Start Practice Test'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="take-test-page">
      {/* Test Header */}
      <div className="test-header">
        <div className="test-info">
          <h2>{testData.title}</h2>
          <div className="test-badges">
            <Badge variant="primary">{testData.subject}</Badge>
            <Badge variant="secondary">{testData.examType}</Badge>
          </div>
        </div>

        <div className="test-controls">
          <div className="timer">
            <FiClock className="timer-icon" />
            <span className={`time-display ${getTimeColor()}`}>
              {formatTime(timeRemaining)}
            </span>
          </div>

          <div className="control-buttons">
            {isTestPaused ? (
              <Button variant="success" size="sm" onClick={resumeTest}>
                <FiPlay />
                Resume
              </Button>
            ) : (
              <Button variant="warning" size="sm" onClick={pauseTest}>
                <FiPause />
                Pause
              </Button>
            )}
            
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                console.log('Submit button clicked!'); // Debug log
                console.log('showSubmitDialog state before:', showSubmitDialog); // Debug log
                setShowSubmitDialog(prev => {
                  console.log('Setting showSubmitDialog from', prev, 'to true');
                  return true;
                });
              }}
              disabled={submitting}
            >
              <FiSend />
              Submit Test
            </Button>
          </div>
        </div>
      </div>

      {/* Test Content */}
      <div className="test-content">
        {/* Question Navigation */}
        <div className="question-navigation">
          <div className="nav-header">
            <h4>Questions</h4>
            <span>{Object.keys(answers).length}/{questions.length} answered</span>
          </div>
          <div className="question-grid">
            {questions.map((_, index) => (
              <button
                key={index}
                className={`question-nav-btn ${index === currentQuestionIndex ? 'active' : ''} ${getQuestionStatus(index)}`}
                onClick={() => navigateToQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question Display */}
        <div className="question-display">
          {isTestPaused ? (
            <div className="paused-overlay">
              <div className="paused-content">
                <FiPause className="paused-icon" />
                <h3>Test Paused</h3>
                <p>Click Resume to continue with your test</p>
                <Button variant="success" onClick={resumeTest}>
                  <FiPlay />
                  Resume Test
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="question-header">
                <div className="exam-paper-header-strip">
                  <span className="paper-type">
                    {currentQuestion?.sectionLabel ? `${currentQuestion.sectionLabel.toUpperCase()}` : 'PAPER 1'}
                    {' · OBJECTIVE'}
                  </span>
                  <span className="paper-duration">{testData.timeLimit} minutes</span>
                  <span className="paper-page">Question {currentQuestionIndex + 1} of {questions.length}</span>
                </div>
                <div className="question-info">
                  <span className="question-number">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <div className="question-controls">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                    >
                      <FiArrowLeft />
                      Previous
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFlagQuestion(currentQuestionIndex)}
                      className={flaggedQuestions.has(currentQuestionIndex) ? 'flagged' : ''}
                    >
                      <FiFlag />
                      {flaggedQuestions.has(currentQuestionIndex) ? 'Flagged' : 'Flag'}
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextQuestion}
                      disabled={currentQuestionIndex === questions.length - 1}
                    >
                      Next
                      <FiArrowRight />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="question-content exam-paper-question-block">
                {currentQuestion.sectionLabel && (currentQuestionIndex === 0 || (questions[currentQuestionIndex - 1] && questions[currentQuestionIndex - 1].sectionLabel !== currentQuestion.sectionLabel)) && (
                  <div className="question-section-header" role="heading" aria-level={2}>
                    {currentQuestion.sectionLabel}
                  </div>
                )}
                <div className="question-number-paper">
                  {currentQuestionIndex + 1}.
                </div>
                <div className="question-text">
                  {typeof currentQuestion.question === 'string' 
                    ? currentQuestion.question 
                    : currentQuestion.question?.text || currentQuestion.question?.label || 'Question text not available'}
                </div>

                <div className="question-options">
                  {currentQuestion.options.map((option, index) => {
                    const letter = option?.label || String.fromCharCode(65 + index);
                    const text = typeof option === 'string' ? option : (option?.text || option?.label || `Option ${index + 1}`);
                    return (
                      <label
                        key={index}
                        className={`option-label ${answers[currentQuestionIndex] === index ? 'selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestionIndex}`}
                          value={index}
                          checked={answers[currentQuestionIndex] === index}
                          onChange={() => handleAnswerSelect(currentQuestionIndex, index)}
                        />
                        <span className="option-letter">{letter}.</span>
                        <span className="option-text">{text}</span>
                      </label>
                    );
                  })}
                </div>

                {/* Show explanation if available (for practice mode) */}
                {currentQuestion.explanation && (
                  <div className="explanation-section">
                    {/* Only show toggle button in normal mode, not in explanation mode */}
                    {examMode !== 'explanation' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleExplanation(currentQuestionIndex)}
                      >
                        {showExplanation[currentQuestionIndex] ? <FiEyeOff /> : <FiEye />}
                        {showExplanation[currentQuestionIndex] ? 'Hide' : 'Show'} Explanation
                      </Button>
                    )}
                    
                    {showExplanation[currentQuestionIndex] && (
                      <div className="explanation-content">
                        <strong>Explanation:</strong>
                        {typeof currentQuestion.explanation === 'string' ? (
                          <p>{currentQuestion.explanation}</p>
                        ) : currentQuestion.explanation && typeof currentQuestion.explanation === 'object' ? (
                          <div>
                            {currentQuestion.explanation.text && (
                              <p>{currentQuestion.explanation.text}</p>
                            )}
                            {currentQuestion.explanation.steps && currentQuestion.explanation.steps.length > 0 && (
                              <div className="explanation-steps">
                                <strong>Steps:</strong>
                                <ol>
                                  {currentQuestion.explanation.steps.map((step, index) => (
                                    <li key={index}>
                                      {step.description}
                                      {step.formula && (
                                        <div className="formula">{step.formula}</div>
                                      )}
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            {currentQuestion.explanation.references && currentQuestion.explanation.references.length > 0 && (
                              <div className="explanation-references">
                                <strong>References:</strong>
                                <ul>
                                  {currentQuestion.explanation.references.map((ref, index) => (
                                    <li key={index}>
                                      {ref.url ? (
                                        <a href={ref.url} target="_blank" rel="noopener noreferrer">
                                          {ref.title}
                                        </a>
                                      ) : (
                                        ref.title
                                      )}
                                      {ref.page && <span> (Page {ref.page})</span>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p>No explanation available</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="question-navigation-controls">
                <Button
                  variant="outline"
                  onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  <FiArrowLeft />
                  Previous
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
                  disabled={currentQuestionIndex === questions.length - 1}
                >
                  Next
                  <FiArrowRight />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      {console.log('Rendering ConfirmDialog section, showSubmitDialog:', showSubmitDialog)}
      {showSubmitDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          {console.log('ConfirmDialog should be visible now')}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3>Submit Practice Test</h3>
            <p>Are you sure you want to submit your test? You have answered {Object.keys(answers).length} out of {questions.length} questions.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button
                variant="outline"
                onClick={() => {
                  console.log('Cancel clicked');
                  setShowSubmitDialog(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  console.log('Submit confirmed');
                  setShowSubmitDialog(false);
                  handleSubmitTest();
                }}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Test'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakePracticeTest;